// Grid Layout Types for react-grid-layout based dashboard

// Grid item position — matches react-grid-layout's Layout item shape
export interface GridItemPosition {
  i: string;       // Unique item ID (widget ID or tab group ID)
  x: number;       // Grid column position (0-11)
  y: number;       // Grid row position
  w: number;       // Width in grid columns
  h: number;       // Height in grid rows
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean; // If true, item cannot be dragged or resized
}

// Tab group — a grid item that contains multiple widgets as switchable tabs
export interface TabGroupConfig {
  id: string;                  // Tab group ID (used as the grid item "i")
  widgetIds: string[];         // Ordered list of widget IDs in this tab group
  activeWidgetId?: string;     // Currently selected tab (defaults to first)
}

// Top-level layout config — replaces FlexLayout IJsonModel in Dashboard.layoutConfig
export interface GridLayoutConfig {
  version: 2;                         // Schema version for migration detection
  cols: number;                       // Grid columns
  rows: number;                       // Grid rows (fixed, fills container height)
  layout: GridItemPosition[];         // Positions for all grid items
  tabGroups: TabGroupConfig[];        // Tab group definitions
}

// Default grid layout constants
export const DEFAULT_COLS = 24;
export const DEFAULT_ROWS = 24;
export const DEFAULT_WIDGET_W = 6;
export const DEFAULT_WIDGET_H = 4;
export const DEFAULT_MIN_W = 3;
export const DEFAULT_MIN_H = 3;
export const DEFAULT_TAB_GROUP_MIN_W = 3;
export const DEFAULT_TAB_GROUP_MIN_H = 3;

// Type guard: is a grid item a tab group?
export function isTabGroup(
  itemId: string,
  config: GridLayoutConfig
): boolean {
  return config.tabGroups.some(g => g.id === itemId);
}

// Get tab group config by ID
export function getTabGroup(
  itemId: string,
  config: GridLayoutConfig
): TabGroupConfig | undefined {
  return config.tabGroups.find(g => g.id === itemId);
}

// Create a default empty grid layout
export function getDefaultGridLayout(): GridLayoutConfig {
  return {
    version: 2,
    cols: DEFAULT_COLS,
    rows: DEFAULT_ROWS,
    layout: [],
    tabGroups: [],
  };
}

// Build a grid layout from a list of widget IDs, arranged in 2-column layout
export function buildGridFromWidgets(
  widgetIds: string[],
): GridLayoutConfig {
  const layout: GridItemPosition[] = widgetIds.map((id, index) => ({
    i: id,
    x: (index % 2) * 6,
    y: Math.floor(index / 2) * DEFAULT_WIDGET_H,
    w: DEFAULT_WIDGET_W,
    h: DEFAULT_WIDGET_H,
    minW: DEFAULT_MIN_W,
    minH: DEFAULT_MIN_H,
  }));

  return {
    version: 2,
    cols: DEFAULT_COLS,
    rows: DEFAULT_ROWS,
    layout,
    tabGroups: [],
  };
}

// Check if two grid items overlap
function itemsOverlap(a: GridItemPosition, b: GridItemPosition): boolean {
  // No overlap if one is fully left, right, above, or below the other
  if (a.x + a.w <= b.x) return false; // a is left of b
  if (b.x + b.w <= a.x) return false; // b is left of a
  if (a.y + a.h <= b.y) return false; // a is above b
  if (b.y + b.h <= a.y) return false; // b is above a
  return true;
}

// Find all overlapping widget pairs in a layout.
// Returns array of [id1, id2] pairs that overlap.
export function findOverlaps(layout: GridItemPosition[]): [string, string][] {
  const overlaps: [string, string][] = [];
  for (let i = 0; i < layout.length; i++) {
    for (let j = i + 1; j < layout.length; j++) {
      if (itemsOverlap(layout[i], layout[j])) {
        overlaps.push([layout[i].i, layout[j].i]);
      }
    }
  }
  return overlaps;
}

// Calculate the next available Y position in the grid
export function getNextY(layout: GridItemPosition[]): number {
  if (layout.length === 0) return 0;
  return layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
}
